import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';

/**
 * Patička dialogu: řada tlačítek pod obsahem dialogu (p-dialog, p-confirmdialog).
 *
 * Dvě podoby použití:
 * - projekce obsahu – vlastní tlačítka jako dřív: `<ui-dialog-footer><button pButton …></ui-dialog-footer>`;
 * - vestavěná dvojice Potvrdit/Zrušit – zadá se `submitLabel`, komponenta vykreslí tlačítko odeslání
 *   (`type="submit"`, patička tedy patří dovnitř `ui-form`) a tlačítko zrušení, které vydá `(handleCancel)`.
 *   `data-cy` tlačítek se zadává přímo (`submitCy`, `cancelCy`), nebo se odvodí z `cyPrefix` jako
 *   `<prefix>-submit` / `<prefix>-zrusit`. Projekce obsahu se vykreslí za vestavěnými tlačítky.
 *
 * Výchozí rozvržení (flex na střed, mezera mezi tlačítky, horní okraj) je v libs/shared/ui/components/src/components.css
 * jako pravidlo na značku ve vrstvě `components`; odchylky se píšou utilitou na místě použití, např.
 * `<ui-dialog-footer class="justify-start p-1">`.
 */
@Component({
  selector: 'ui-dialog-footer',
  imports: [ButtonModule],
  template: `
    @if (submitLabel(); as label) {
      <button
        pButton
        [type]="submitType()"
        [disabled]="submitDisabled()"
        [attr.data-cy]="submitCyValue()"
        (click)="onSubmitClick()">
        <span pButtonLabel>{{ label }}</span>
      </button>
      <button
        pButton
        type="button"
        class="p-button-outlined"
        [attr.data-cy]="cancelCyValue()"
        (click)="handleCancel.emit()">
        <span pButtonLabel>{{ cancelLabel() }}</span>
      </button>
    }
    <ng-content />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFooterComponent {
  /** Popisek tlačítka odeslání; bez něj se vestavěná tlačítka nevykreslí (jen projekce obsahu). */
  readonly submitLabel = input<string>();
  readonly cancelLabel = input('Zrušit');
  /** `submit` odešle obalující formulář, `button` jen vydá `(handleSubmit)`. */
  readonly submitType = input<'submit' | 'button'>('submit');
  readonly submitDisabled = input(false);
  /** Předpona `data-cy`: `<prefix>-submit` a `<prefix>-zrusit`, pokud není zadáno `submitCy` / `cancelCy`. */
  readonly cyPrefix = input<string>();
  readonly submitCy = input<string>();
  readonly cancelCy = input<string>();

  readonly handleSubmit = output<void>();
  readonly handleCancel = output<void>();

  /**
   * Klik na tlačítko odeslání. Typ `submit` nechá prohlížeč odeslat obalující formulář (handler nesmí vracet
   * `false` – Angular by zavolal `preventDefault()` a formulář by se neodeslal), typ `button` vydá `(handleSubmit)`.
   */
  protected onSubmitClick(): void {
    if (this.submitType() === 'button') {
      this.handleSubmit.emit();
    }
  }

  protected readonly submitCyValue = computed(
    () => this.submitCy() ?? (this.cyPrefix() ? `${this.cyPrefix()}-submit` : undefined)
  );
  protected readonly cancelCyValue = computed(
    () => this.cancelCy() ?? (this.cyPrefix() ? `${this.cyPrefix()}-zrusit` : undefined)
  );
}
